import styled from 'styled-components';
import { compareObjects } from '../../../../functions/utils/comapareObjects';
import { LauncherStatesData } from '../../../../hooks/stores/launcherStatesStore';
import { UIData, useUIStore } from '../../../../hooks/stores/uiStore';
import { VisibilityButton } from '../../stateViewController/VisibilityButton';
import { useScene } from '../../../../context/sceneContext';

export const AnyTaskViewButton = () => {
  const scene = useScene();
  const [UIData, setUIData] = useUIStore<UIData>();
  const useDirectVisibility = UIData.useDirectVisibility;

  const visibility = (state: [keyof LauncherStatesData | null, string | null]) => {
    if (!useDirectVisibility) {
      return scene.getSelectedElementsDirectVisibility(state);
    }

    // When useDirectVisibility is true, check all individual task states
    switch (state[0]) {
      case 'isAnyTaskInProgress': {
        const taskStates = [
          'isInstallTaskInProgress',
          'isUpdateTaskInProgress',
          'isRepairTaskInProgress',
          'isMoveTaskInProgress',
          'isUninstallTaskInProgress',
        ] as const;
        
        return taskStates.some(taskState => 
          scene.getSelectedElementsDirectVisibility([taskState, 'true']) !== false
        );
      }
      case 'isAnyTaskPaused': {
        const taskStates = [
          'isInstallTaskPaused',
          'isUpdateTaskPaused',
          'isRepairTaskPaused',
          'isMoveTaskPaused',
          'isUninstallTaskPaused',
        ] as const;
        
        return taskStates.some(taskState => 
          scene.getSelectedElementsDirectVisibility([taskState, 'true']) !== false
        );
      }
      case 'hasAnyTaskFailed': {
        const taskStates = [
          'hasInstallTaskFailed',
          'hasUpdateTaskFailed',
          'hasRepairTaskFailed',
          'hasMoveTaskFailed',
          'hasUninstallTaskFailed',
        ] as const;
        
        return taskStates.some(taskState => 
          scene.getSelectedElementsDirectVisibility([taskState, 'true']) !== false
        );
      }
      default:
        return scene.getSelectedElementsDirectVisibility(state);
    }
  };

  const isActive = (state: [keyof LauncherStatesData | null, string | null]) => {
    switch (state[0]) {
      case 'isAnyTaskInProgress':
        return compareObjects(UIData.currentLauncherState, ['isAnyTaskInProgress', 'true']);
      case 'isAnyTaskPaused':
        return compareObjects(UIData.currentLauncherState, ['isAnyTaskPaused', 'true']);
      case 'hasAnyTaskFailed':
        return compareObjects(UIData.currentLauncherState, ['hasAnyTaskFailed', 'true']);
    }

    return compareObjects(UIData.currentLauncherState, ['isAnyTaskInProgress', 'true']);
  };

  const currentForState = (
    state: [keyof LauncherStatesData | null, string | null]
  ): [keyof LauncherStatesData | null, string | null] => {
    switch (state[0]) {
      case 'isAnyTaskInProgress':
        return ['isAnyTaskInProgress', 'true'];
      case 'isAnyTaskPaused':
        return ['isAnyTaskPaused', 'true'];
      case 'hasAnyTaskFailed':
        return ['hasAnyTaskFailed', 'true'];
    }

    return ['isAnyTaskInProgress', 'true'];
  };

  return (
    <Container
      $isLeftContainerVisible={
        visibility(['isAnyTaskInProgress', 'true']) === false || 
        (visibility(['isAnyTaskInProgress', 'true']) !== undefined && !useDirectVisibility)
      }
      $isRightContainerVisible={
        isActive(['isAnyTaskPaused', 'true']) ||
        isActive(['hasAnyTaskFailed', 'true']) ||
        (UIData.selectedElementIDs.length > 0 &&
          (visibility(['isAnyTaskPaused', 'true']) === false ||
           visibility(['hasAnyTaskFailed', 'true']) === false ||
           (!useDirectVisibility && (
             visibility(['isAnyTaskPaused', 'true']) !== undefined ||
             visibility(['hasAnyTaskFailed', 'true']) !== undefined
           ))))
      }
    >
      <SubButtonsLeftContainer className="subButtonsLeft">
        <VisibilityButton forState={currentForState(UIData.currentLauncherState)} onlyDirectVisibility />
      </SubButtonsLeftContainer>

      <SubButtonsRightContainer className="subButtonsRight">
        <SubButton
          onClick={() => {
            setUIData({ currentLauncherState: ['isAnyTaskPaused', 'true'] });
          }}
          $isActive={isActive(['isAnyTaskPaused', 'true'])}
          $computedVisibility={visibility(['isAnyTaskPaused', 'true'])}
          $useDirectVisibility={useDirectVisibility}
        >
          <i
            className="icon-paused"
            style={{
              fontSize: '12px',
              lineHeight: '12px',
            }}
          />
        </SubButton>
        <SubButton
          onClick={() => {
            setUIData({ currentLauncherState: ['hasAnyTaskFailed', 'true'] });
          }}
          $isActive={isActive(['hasAnyTaskFailed', 'true'])}
          $computedVisibility={visibility(['hasAnyTaskFailed', 'true'])}
          $useDirectVisibility={useDirectVisibility}
        >
          <i
            className="icon-failed"
            style={{
              fontSize: '12px',
              lineHeight: '12px',
            }}
          />
        </SubButton>
      </SubButtonsRightContainer>

      <MainButton
        $isActive={isActive(['isAnyTaskInProgress', 'true'])}
        $computedVisibility={visibility(['isAnyTaskInProgress', 'true'])}
        $useDirectVisibility={useDirectVisibility}
        onClick={() => {
          setUIData({ currentLauncherState: ['isAnyTaskInProgress', 'true'] });
        }}
      >
        <i className={'icon-inprogress'} style={{ fontSize: '20px', lineHeight: '20px' }} />
      </MainButton>
      <Label
        $isActive={
          isActive(['isAnyTaskInProgress', 'true']) ||
          isActive(['isAnyTaskPaused', 'true']) ||
          isActive(['hasAnyTaskFailed', 'true'])
        }
      >
        Any Task
      </Label>
    </Container>
  );
};

const Container = styled.div<{ $isLeftContainerVisible?: boolean; $isRightContainerVisible?: boolean }>`
  position: relative;
  margin-bottom: 15px;

  box-sizing: border-box;
  width: 50px;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;

  .subButtonsRight {
    transition: opacity var(--transition-2);
    opacity: 0;
  }

  .subButtonsLeft {
    transition: opacity var(--transition-2);
    opacity: 0;
  }

  &:hover {
    .subButtonsRight,
    .subButtonsLeft {
      opacity: 1;
    }
  }

  ${({ $isLeftContainerVisible }) =>
    $isLeftContainerVisible &&
    `
      .subButtonsLeft {
        opacity: 1;
      }
    `}

  ${({ $isRightContainerVisible }) =>
    $isRightContainerVisible &&
    `
      .subButtonsRight {
        opacity: 1;
      }
    `}
`;

const SubButtonsLeftContainer = styled.div`
  height: 100%;
  position: absolute;
  left: 3px;
  translate: -100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
`;
const SubButtonsRightContainer = styled.div`
  height: 100%;
  position: absolute;
  right: 3px;
  translate: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const MainButton = styled.div<{
  $isActive?: boolean;
  $isCurrentStateDifferentFromDisplayed?: boolean;
  $computedVisibility?: boolean;
  $useDirectVisibility?: boolean;
}>`
  --text-color: var(--main-2);
  --text-color-hovered: var(--main-1);
  --border-color: var(--neutral-3);

  ${({ $isActive, $isCurrentStateDifferentFromDisplayed }) =>
    $isActive &&
    `
      --border-color: var(${$isCurrentStateDifferentFromDisplayed ? '--primary-2' : '--primary-1'});
      --text-color: var(${$isCurrentStateDifferentFromDisplayed ? '--primary-2' : '--primary-1'});
      --text-color-hovered: var(--primary-1);
    `}

  ${({ $computedVisibility }) =>
    $computedVisibility === false &&
    `
      --text-color: var(--error-1);
      --text-color-hovered: var(--error-1);
    `}

  cursor: pointer;
  width: 40px;
  height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  color: var(--text-color);
  background-color: var(--neutral-2);
  border: 1px solid var(--border-color);
  border-radius: 50%;

  transition:
    opacity var(--transition-2),
    background-color var(--transition-2),
    color var(--transition-2),
    border var(--transition-2);
  &:hover {
    background-color: var(--neutral-3);
    color: var(--text-color-hovered);
  }
`;

const Label = styled.div<{
  $isActive: boolean;
}>`
  position: absolute;
  bottom: -25px;
  padding: 3px 7px;
  margin-bottom: 7px;

  font-family: var(--font-1);
  font-size: 12px;
  text-align: center;
  white-space: nowrap;
  text-shadow: 0px 0px 2px #000;
  color: var(--main-2);

  background-color: var(--neutral-1);
  border-radius: 5px;
  overflow: hidden;

  transition: color var(--transition-2);
  ${({ $isActive }) => $isActive && `color: var(--primary-1);`}
`;

const SubButton = styled.div<{
  $isActive?: boolean;
  $isCurrentStateDifferentFromDisplayed?: boolean;
  $computedVisibility?: boolean;
  $useDirectVisibility?: boolean;
}>`
  --text-color: var(--main-2);
  --text-color-hovered: var(--main-1);
  --border-color: var(--neutral-3);

  ${({ $isActive, $isCurrentStateDifferentFromDisplayed }) =>
    $isActive &&
    `
      --border-color: var(${$isCurrentStateDifferentFromDisplayed ? '--primary-2' : '--primary-1'});
      --text-color: var(${$isCurrentStateDifferentFromDisplayed ? '--primary-2' : '--primary-1'});
      --text-color-hovered: var(--primary-1);
    `}

  ${({ $computedVisibility }) =>
    $computedVisibility === false &&
    `
    --text-color: var(--error-1);
    --text-color-hovered: var(--error-1);
  `}

  box-sizing: border-box;
  cursor: pointer;
  width: 22px;
  height: 22px;

  display: flex;
  justify-content: center;
  align-items: center;

  color: var(--text-color);
  background-color: var(--neutral-2);
  border: 1px solid var(--border-color);
  border-radius: 50%;

  transition:
    background-color var(--transition-2),
    color var(--transition-2);
  &:hover {
    background-color: var(--neutral-3);
    color: var(--text-color-hovered);
  }
`;
